import React, { useState, useEffect } from 'react';
import { Table, ColumnDefinition } from '@/UIlibrary/Table/Table';
import { GRAY_COLORS, BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { ChevronDown, ChevronUp, AlertCircle, X, Calendar } from 'react-feather';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import {
	useCreditBureauAuthorizationsQuery,
	CreditBureauAuthorization,
	SortDirection,
	CreditBureauAuthorizationSort,
	CreditBureauAuthorizationFilter
} from '@/graphql/__generated__/onboarding';
import Button from '@/UIlibrary/Button/Button';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { es } from 'date-fns/locale';
import { formatDateTime } from '@/utils/utils';
import EmojiIcon from '@/stories/Components/EmojiIcon/EmojiIcon';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 10000;

type BureauAuthorizationsTableData = CreditBureauAuthorization[];

const BureauAuthorizationsTable: React.FC = () => {
	const t = useTranslations('BureauAuthorizations');
	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
	const [openConfirmDownloadModal, setOpenConfirmDownloadModal] = useState(false);
	const [downloadType, setDownloadType] = useState<'csv' | 'excel'>('csv');
	const [loadingDownload, setLoadingDownload] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined;
		endDate: Date | undefined;
		key: string;
	}>({
		startDate: undefined,
		endDate: undefined,
		key: 'selection'
	});

	const getFilter = (): CreditBureauAuthorizationFilter | undefined => {
		const filter: CreditBureauAuthorizationFilter = {};

		if (dateRange.startDate || dateRange.endDate) {
			filter.requestDate = {};

			if (dateRange.startDate) {
				// Set start date to beginning of the day (00:00:00)
				const startDate = new Date(dateRange.startDate);
				startDate.setHours(0, 0, 0, 0);
				filter.requestDate.from = startDate.toISOString();
			}

			if (dateRange.endDate) {
				// Set end date to end of the day (23:59:59.999)
				const endDate = new Date(dateRange.endDate);
				endDate.setHours(23, 59, 59, 999);
				filter.requestDate.to = endDate.toISOString();
			}
		}

		// Return undefined if no filters applied
		return Object.keys(filter).length ? filter : undefined;
	};

	const getCurrentSort = (): CreditBureauAuthorizationSort => ({
		['requestDate']: sortDirection
	});

	const { loading, error, data, fetchMore, refetch } = useCreditBureauAuthorizationsQuery({
		client: onboardingClient,
		variables: {
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		},
		notifyOnNetworkStatusChange: true
	});

	// Effect to refetch when filters change
	useEffect(() => {
		refetch({
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		});
	}, [sortDirection, dateRange, refetch]);

	const handleLoadMore = async () => {
		if (!data?.creditBureauAuthorizations?.pageInfo.hasNextPage) return;

		const lastCursor =
			data.creditBureauAuthorizations?.edges?.[data.creditBureauAuthorizations.edges.length - 1]
				?.cursor;
		if (!lastCursor) return;

		try {
			await fetchMore({
				variables: {
					after: lastCursor,
					first: PAGE_SIZE,
					// sorts: [getCurrentSort()],
					filter: getFilter()
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.creditBureauAuthorizations) return prev;

					return {
						creditBureauAuthorizations: {
							...fetchMoreResult.creditBureauAuthorizations,
							edges: [
								...(prev.creditBureauAuthorizations?.edges || []),
								...(fetchMoreResult.creditBureauAuthorizations.edges || [])
							]
						}
					};
				}
			});
		} catch (error) {
			console.error('Error loading more bureau authorizations:', error);
		}
	};

	const handleCsvDownload = async () => {
		setLoadingDownload(true);

		try {
			const headers = [
				'No.',
				'No. de transacción (Referencia del Operador)',
				'Fecha',
				'Nombre',
				'Paterno',
				'Materno',
				'RFC',
				'Calle - No.',
				'Colonia',
				'Ciudad',
				'Edo.',
				'Ingreso nuevamente de NIP',
				'Respuesta a leyenda de autorización',
				'No. folio de consulta BC'
			];

			let csvContent = headers.join(',') + '\n';

			const bureauAuthorizations: BureauAuthorizationsTableData =
				(data?.creditBureauAuthorizations?.edges
					?.map((edge) => edge?.node)
					.filter(Boolean) as CreditBureauAuthorization[]) || [];

			let counter = 1;

			bureauAuthorizations.forEach((auth) => {
				const row = [
					counter,
					`"${String(auth.requestId).replace(/"/g, '""')}"`,
					`"${String(auth.requestDate).replace(/"/g, '""')}"`,
					`"${String(auth.fullName?.split(' ')[0] || '-').replace(/"/g, '""')}"`,
					`"${String(auth.fullName?.split(' ')[1] || '-').replace(/"/g, '""')}"`,
					`"${String(auth.fullName?.split(' ')[2] || '-').replace(/"/g, '""')}"`,
					`"${String(auth.rfc || '-').replace(/"/g, '""')}"`,
					`"${String(`${auth.officialAddress?.street || '-'} - ${auth.officialAddress?.externalNumber || ''}`).replace(/"/g, '""')}"`,
					`"${String(auth.officialAddress?.colony || '-').replace(/"/g, '""')}"`,
					`"${String(auth.officialAddress?.city || '-').replace(/"/g, '""')}"`,
					`"${String(auth.officialAddress?.state || '-').replace(/"/g, '""')}"`,
					`"${String(auth.wasNipRequested ? 'S' : 'N').replace(/"/g, '""')}"`,
					`"${String(auth.wasAuthorized ? 'S' : 'N').replace(/"/g, '""')}"`,
					`"${String(auth.creditBureauReportRequestId || '-').replace(/"/g, '""')}"`
				];
				counter++;
				csvContent += row.join(',') + '\n';
			});

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

			// Create timestamp with date, hours and minutes in local time
			const now = new Date();
			const dateStr = now.toISOString().slice(0, 10);
			const hours = String(now.getHours()).padStart(2, '0');
			const minutes = String(now.getMinutes()).padStart(2, '0');
			const timestamp = `${dateStr}_${hours}.${minutes}`;

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.setAttribute('href', url);
			link.setAttribute('download', `autorizaciones_buro_${timestamp}.csv`);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			setLoadingDownload(false);
		} catch (error) {
			console.error('Error downloading CSV:', error);
			toast.error(t('ErrorDownloadingCSV'));
			setLoadingDownload(false);
		}
	};

	const handleExcelDownload = async () => {
		setLoadingDownload(true);

		try {
			const headers = [
				'No.',
				'No. de transacción (Referencia del Operador)',
				'Fecha',
				'Nombre',
				'Paterno',
				'Materno',
				'RFC',
				'Calle - No.',
				'Colonia',
				'Ciudad',
				'Edo.',
				'Ingreso nuevamente de NIP',
				'Respuesta a leyenda de autorización',
				'No. folio de consulta BC'
			];

			const bureauAuthorizations: BureauAuthorizationsTableData =
				(data?.creditBureauAuthorizations?.edges
					?.map((edge) => edge?.node)
					.filter(Boolean) as CreditBureauAuthorization[]) || [];

			// Prepare data for Excel
			const excelData = [];
			excelData.push(headers); // Add headers as first row

			let counter = 1;
			bureauAuthorizations.forEach((auth) => {
				const row = [
					counter,
					String(auth.requestId || '-'),
					String(auth.requestDate || '-'),
					String(auth.fullName?.split(' ')[0] || '-'),
					String(auth.fullName?.split(' ')[1] || '-'),
					String(auth.fullName?.split(' ')[2] || '-'),
					String(auth.rfc || '-'),
					String(
						`${auth.officialAddress?.street || '-'} - ${auth.officialAddress?.externalNumber || ''}`
					),
					String(auth.officialAddress?.colony || '-'),
					String(auth.officialAddress?.city || '-'),
					String(auth.officialAddress?.state || '-'),
					String(auth.wasNipRequested ? 'S' : 'N'),
					String(auth.wasAuthorized ? 'S' : 'N'),
					String(auth.creditBureauReportRequestId || '-')
				];
				counter++;
				excelData.push(row);
			});

			// Create a worksheet
			const worksheet = XLSX.utils.aoa_to_sheet(excelData);

			// Create timestamp with date, hours and minutes in local time
			const now = new Date();
			const dateStr = now.toISOString().slice(0, 10);
			const hours = String(now.getHours()).padStart(2, '0');
			const minutes = String(now.getMinutes()).padStart(2, '0');
			const timestamp = `${dateStr}_${hours}.${minutes}`;

			// Create a workbook
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Autorizaciones');

			// Generate Excel file
			const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
			const blob = new Blob([excelBuffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});

			// Download file
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.setAttribute('href', url);
			link.setAttribute('download', `autorizaciones_buro_${timestamp}.xlsx`);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			setLoadingDownload(false);
		} catch (error) {
			console.error('Error downloading Excel:', error);
			toast.error(t('ErrorDownloadingExcel'));
			setLoadingDownload(false);
		}
	};

	const handleModalConfirm = () => {
		if (downloadType === 'excel') {
			handleExcelDownload();
		}
		if (downloadType === 'csv') {
			handleCsvDownload();
		}
		setOpenConfirmDownloadModal(false);
	};

	const handleSortChange = () => {
		setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
	};

	const SortIcon = () => {
		const Icon = sortDirection === SortDirection.ASC ? ChevronUp : ChevronDown;
		return <Icon size={16} className="text-gray-500" />;
	};

	const renderCellContent = (content: string) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{content}
		</Typography>
	);

	const renderStreetAndNumber = (bureauAuthorization: CreditBureauAuthorization) => (
		<>
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{bureauAuthorization.officialAddress?.street || '-'}
			</Typography>
			-
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{bureauAuthorization.officialAddress?.externalNumber || ''}
			</Typography>
		</>
	);

	const renderRequestDate = (bureauAuthorization: CreditBureauAuthorization) => {
		if (!bureauAuthorization.requestDate)
			return (
				<Typography size="sm" color={GRAY_COLORS.gray700}>
					-
				</Typography>
			);

		return (
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{bureauAuthorization.requestDate}
			</Typography>
		);
	};

	const columns: ColumnDefinition[] = [
		{
			header: t('ReferenceNumber'),
			key: 'referenceNumber',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.requestId || '-')
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer"
					onClick={handleSortChange}
					role="button"
					tabIndex={0}
				>
					<span>{t('Date')}</span>
					<SortIcon />
				</div>
			),
			key: 'startedAt',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderRequestDate(bureauAuthorization)
		},
		{
			header: t('Name'),
			key: 'name',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.fullName?.split(' ')[0] || '-')
		},
		{
			header: t('LastName'),
			key: 'lastName',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.fullName?.split(' ')[1] || '-')
		},
		{
			header: t('SecondLastName'),
			key: 'secondLastName',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.fullName?.split(' ')[2] || '-')
		},
		{
			header: t('Rfc'),
			key: 'rfc',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.rfc || '-')
		},
		{
			header: t('StreetAndNumber'),
			key: 'streetAndNumber',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderStreetAndNumber(bureauAuthorization)
		},
		{
			header: t('Colony'),
			key: 'colony',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.officialAddress?.colony || '-')
		},
		{
			header: t('City'),
			key: 'city',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.officialAddress?.city || '-')
		},
		{
			header: t('State'),
			key: 'state',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.officialAddress?.state || '-')
		},
		{
			header: t('WasNipRequested'),
			key: 'wasNipRequested',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.wasNipRequested ? 'S' : 'N')
		},
		{
			header: t('WasAuthorized'),
			key: 'wasAuthorized',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.wasAuthorized ? 'S' : 'N')
		},
		{
			header: t('CreditBureauAuthorizationRequestId'),
			key: 'creditBureauAuthorizationRequestId',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.creditBureauReportRequestId || '-')
		},
		{
			header: t('SuccessfulBureauCall'),
			key: 'creditBureauAuthorizationRequestId',
			cellRenderer: (_, bureauAuthorization: CreditBureauAuthorization) =>
				renderCellContent(bureauAuthorization.successful ? 'S' : 'N')
		}
	];

	if (error) {
		return (
			<div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-md">
				<AlertCircle size={16} />
				<Typography size="sm">{t('ErrorLoadingAuthorizations')}</Typography>
			</div>
		);
	}

	const isInitialLoading = loading && !data;
	const bureauAuthorizations: BureauAuthorizationsTableData =
		(data?.creditBureauAuthorizations?.edges
			?.map((edge) => edge?.node)
			.filter(Boolean) as CreditBureauAuthorization[]) || [];
	const totalCount = data?.creditBureauAuthorizations?.count || 0;
	const hasMore = data?.creditBureauAuthorizations?.pageInfo.hasNextPage || false;

	const handleDateRangeChange = (
		rangesByKey: Record<string, { startDate?: Date; endDate?: Date }>
	) => {
		const selection = rangesByKey.selection;
		if (!selection?.startDate || !selection?.endDate) return;
		const { startDate, endDate } = selection;
		setDateRange({
			startDate,
			endDate,
			key: 'selection'
		});
		setShowDatePicker(false);
	};

	const handleClearDateRange = () => {
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	return (
		<>
			<div className="flex flex-col py-4 mt-16 gap-4">
				{/* Date picker section */}
				<div className="relative">
					<div className="flex flex-row items-center justify-between">
						<div className="flex items-center gap-4">
							<Button
								onClick={() => setShowDatePicker(!showDatePicker)}
								size="auto"
								endIcon={<Calendar size={20} />}
								label={
									dateRange.startDate && dateRange.endDate
										? `${formatDateTime(dateRange.startDate.toISOString()).split(' ')[0]} - ${formatDateTime(dateRange.endDate.toISOString()).split(' ')[0]}`
										: t('SelectDateRange')
								}
								dataTestId="dateRangeButton"
							/>
							<span>
								{dateRange.startDate && (
									<Button
										onClick={handleClearDateRange}
										size="auto"
										textOnly
										label={t('ClearDates')}
										endIcon={<X />}
									/>
								)}
							</span>
						</div>
						<div className="flex flex-row gap-4">
							<Button
								label={t('Download')}
								endIcon={<EmojiIcon src="/csv-icon.svg" height={24} width={24} />}
								color="secondary"
								onClick={() => {
									setDownloadType('csv');
									setOpenConfirmDownloadModal(true);
								}}
								disabled={isInitialLoading || bureauAuthorizations.length === 0}
							/>
							<Button
								label={t('Download')}
								size="small"
								onClick={() => {
									setDownloadType('excel');
									setOpenConfirmDownloadModal(true);
								}}
								endIcon={<img src="/excel-file.svg" width={24} height={24} />}
								color="success"
								disabled={isInitialLoading || bureauAuthorizations.length === 0}
							/>
						</div>
					</div>

					{showDatePicker && (
						<div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg">
							<DateRange
								showMonthAndYearPickers={true}
								showDateDisplay={false}
								ranges={[dateRange]}
								months={1}
								onChange={handleDateRangeChange}
								locale={es}
								moveRangeOnFirstSelection={false}
								direction="horizontal"
								rangeColors={[BRAND_COLORS.brand500]}
								minDate={new Date(2020, 0, 1)}
								maxDate={new Date()}
							/>
						</div>
					)}
				</div>

				<div className="custom-scrollbar max-h-[50vh] overflow-y-auto">
					{isInitialLoading ? (
						<div className="flex justify-center items-center h-64">
							<div className="loader" />
						</div>
					) : (
						<>
							<Table
								dataTestId="AuthorizationsTable"
								columns={columns}
								data={bureauAuthorizations}
							/>

							{hasMore && (
								<div className="text-center py-4">
									<Button
										onClick={handleLoadMore}
										size="auto"
										label={t('LoadMore')}
										disabled={loading}
									/>
								</div>
							)}

							{!loading && bureauAuthorizations.length === 0 && (
								<div className="text-center py-4 text-gray-500">{t('NoResults')}</div>
							)}
						</>
					)}
				</div>

				{loading && !isInitialLoading && (
					<div className="flex justify-center py-4">
						<div className="loader" />
					</div>
				)}

				{!isInitialLoading && totalCount > 0 && (
					<div className="mt-1 ml-5 border-t border-b py-3">
						<Typography size="sm" color={GRAY_COLORS.gray600}>
							{t('ShowingResults')}{' '}
							<span className="font-medium">
								{bureauAuthorizations.length.toLocaleString()} {t('Of')}{' '}
								{totalCount.toLocaleString()}
							</span>
						</Typography>
					</div>
				)}
			</div>
			<ConfirmModal
				isOpen={openConfirmDownloadModal}
				title={t('ConfirmDownload')}
				onClose={() => setOpenConfirmDownloadModal(false)}
				onCancel={() => setOpenConfirmDownloadModal(false)}
				onConfirm={handleModalConfirm}
				cancelBtnLoading={loadingDownload}
				confirmBtnLoading={loadingDownload}
				confirmActionText={downloadType === 'csv' ? t('DownloadCSV') : t('DownloadExcel')}
			>
				<Typography size="sm" color={GRAY_COLORS.gray700}>
					{t('ConfirmDownloadMessage')}
				</Typography>
			</ConfirmModal>
		</>
	);
};

export default BureauAuthorizationsTable;
